import { useId } from "react";
import type React from "react";
import {
  ELEPHANT_POSES,
  elephantAssetSrc,
  type ElephantLayerMotion,
  type ElephantPose as ElephantPoseName,
} from "@/lib/illustration/elephantPoses";

/**
 * 場面ごとの動くゾウ（PRODUCT_DEV-1201）。定義は lib/illustration/elephantPoses.ts。
 *
 * 体の PNG の上に、小物だけの PNG（同じ大きさ）を重ねて動かす。ServiceIllustration の
 * analyzing（軌道リング）と同じく、動きは CSS アニメーション（transform / opacity）だけで作る。
 * JS が詰まっていてもコンポジタ側で進み、裏タブから戻っても止まったままにならないため。
 *
 * 「動きを減らす」設定のとき: お祝いの動き（弾む・ピッ・キラッ・紙ひこうき）は止めて、
 * 小物は最後の形で出す。待ちの点の輪だけは止めずに遅くする（止めると「固まった」に見える。PD-660）。
 */

const MOTION_CLASS: Record<ElephantLayerMotion, string> = {
  pips: "motion-safe:animate-zouPips",
  twinkle: "motion-safe:animate-zouTwinkle",
  spin: "animate-zouSpin motion-reduce:animate-zouSpinCalm",
  launch: "motion-safe:animate-zouLaunch",
};

interface ElephantPoseProps {
  pose: ElephantPoseName;
  /** 外枠の幅(px) */
  width: number;
  className?: string;
  /** 既定は装飾扱い（空 alt）。意味を伝えたいときだけ渡す。 */
  alt?: string;
}

export const ElephantPose: React.FC<ElephantPoseProps> = ({
  pose,
  width,
  className,
  alt = "",
}) => {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const def = ELEPHANT_POSES[pose];
  const { width: w, height: h } = def.size;
  const layerClass = "absolute inset-0 h-full w-full select-none";

  return (
    <div
      role={alt ? "img" : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      data-pose={pose}
      className={`relative${def.enter ? " motion-safe:animate-zouEnter" : ""}${className ? ` ${className}` : ""}`}
      style={{ width, aspectRatio: `${w} / ${h}` }}
    >
      <img
        src={elephantAssetSrc(def.body)}
        alt=""
        width={w}
        height={h}
        className={layerClass}
        draggable={false}
      />
      {def.layers.map((layer) => (
        <img
          key={layer.src}
          src={elephantAssetSrc(layer.src)}
          alt=""
          width={w}
          height={h}
          className={`${layerClass} ${MOTION_CLASS[layer.motion]}`}
          style={{ transformOrigin: `${layer.origin.x}% ${layer.origin.y}%` }}
          draggable={false}
        />
      ))}
      {def.glint && (
        // 眼鏡のレンズに斜めの光を走らせる。レンズの円で切り抜いた中を白い帯が横切る
        <svg
          viewBox={`0 0 ${w} ${h}`}
          className="absolute inset-0 h-full w-full"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <clipPath id={`zou-lens-${uid}`}>
              <circle cx={def.glint.cx} cy={def.glint.cy} r={def.glint.r} />
            </clipPath>
          </defs>
          {/* 傾きは <g> に持たせる。rect に transform 属性を書くと CSS アニメーションの transform に上書きされる */}
          <g clipPath={`url(#zou-lens-${uid})`}>
            <g transform={`rotate(30 ${def.glint.cx} ${def.glint.cy})`}>
              <rect
                x={def.glint.cx - def.glint.r * 0.3}
                y={def.glint.cy - def.glint.r * 1.6}
                width={def.glint.r * 0.6}
                height={def.glint.r * 3.2}
                fill="#fff"
                opacity={0.8}
                className="hidden motion-safe:inline motion-safe:animate-zouGlint"
                style={{ transformBox: "fill-box" }}
              />
            </g>
          </g>
        </svg>
      )}
    </div>
  );
};
